"use client";

import type { ComponentPropsWithoutRef } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { ColorSelect } from "@/components/core/ColorSelect";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

interface RHFColorSelectProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<
      ComponentPropsWithoutRef<typeof ColorSelect>,
      "value" | "onChange" | "name"
    > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
}

export default function RHFColorSelect<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  multiple = false,
  ...other
}: RHFColorSelectProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || (multiple ? [] : "")}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <div className={cn("space-y-2 w-full", containerClass)}>
            <ColorSelect
              value={field.value}
              onChange={field.onChange}
              multiple={multiple}
              {...other}
            />
            {!!helperText && (
              <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                {helperText}
              </p>
            )}
            {!!error && (
              <p className="text-xs leading-tight text-semantic-error ml-1.5">
                {error?.message}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
